import {
  Box,
  Flex,
  IconButton,
  Select,
  Spinner,
  Stack,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";
import {
  matchPath,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  IoPeopleOutline,
  IoSettingsOutline,
  IoShareSocialOutline,
  IoBarChartOutline,
  IoClipboardOutline,
  IoAnalytics,
  IoStopwatchOutline,
  IoCashOutline,
} from "react-icons/io5";
import { MdEvent } from "react-icons/md";
import { NavButton } from "../../NavButton";
import {
  KYDAdminEventDetailNav,
  KYDAdminEventDetailNavIcon,
} from "@common/interfaces/KYDEvent";
import { FaCircle, FaStopwatch } from "react-icons/fa";
import { IconType } from "react-icons/lib";

const AvailableIcons: Record<KYDAdminEventDetailNavIcon, IconType> = {
  MdEvent: MdEvent,
  IoPeopleOutline: IoPeopleOutline,
  IoShareSocialOutline: IoShareSocialOutline,
  IoBarChartOutline: IoBarChartOutline,
  IoSettingsOutline: IoSettingsOutline,
  IoClipboardOutline: IoClipboardOutline,
  IoAnalytics: IoAnalytics,
  IoStopwatchOutline: IoStopwatchOutline,
  IoCashOutline: IoCashOutline,
  default: FaCircle,
};

const getMenuItems = ({
  nav = [],
}: {
  nav: KYDAdminEventDetailNav[];
}): {
  url: (eventId: string) => string;
  label: string;
  icon: IconType;
}[] => {
  if (!nav.length) return [];

  return nav.map((nav) => ({
    icon:
      nav.icon in AvailableIcons
        ? AvailableIcons[nav.icon]
        : AvailableIcons.default,
    label: nav.text,
    url: (eventId: string) => nav.url,
  }));
};

export const DesktopSideBar = ({
  show = true,
  nav,
  isLoading = false,
}: {
  show: boolean;
  isLoading: boolean;
  nav: KYDAdminEventDetailNav[];
}) => {
  const navigate = useNavigate();
  const { event_id } = useParams();
  const [isCollapsed, setIsCollapsed] = useState(true);
  const location = useLocation();

  const menuItems = useMemo(() => {
    return getMenuItems({ nav });
  }, [nav]);

  const colorModeValue = useColorModeValue("md", "sm-dark");

  if (!show) {
    return null;
  }

  return (
    <Box>
      <motion.div
        transition={{
          type: "tween",
          duration: 0.5,
        }}
        animate={{ width: !isCollapsed ? "50px" : "150px" }}
        initial={false}
        style={{
          display: "flex",
          position: "relative",
          height: "100%",
        }}
      >
        <AnimatePresence>
          {isCollapsed ? (
            <motion.div
              transition={{
                type: "tween",
                duration: 0.3,
              }}
              style={{
                position: "absolute",
                height: "100%",
                left: 0,
                top: 0,
                zIndex: 10,
              }}
              key={"event-detail-sidebar"}
              initial={{ translateX: "-100%" }}
              animate={{ translateX: 0 }}
              exit={{ translateX: "-100%" }}
            >
              <Flex as="section" height="100%" bg="bg-canvas">
                <Flex
                  flex="1"
                  overflowY="auto"
                  bg="white"
                  boxShadow={colorModeValue}
                >
                  <Stack justify="space-between" spacing="1">
                    <Stack spacing={{ base: "5", sm: "6" }} shouldWrapChildren>
                      <VStack h={16} pt={4}>
                        {isCollapsed ? (
                          <IconButton
                            variant="ghost"
                            aria-label="Open"
                            icon={<FiArrowLeft />}
                            onClick={() => setIsCollapsed(false)}
                          />
                        ) : (
                          <IconButton
                            aria-label="Close"
                            variant="ghost"
                            icon={<FiArrowRight />}
                            onClick={() => setIsCollapsed(true)}
                          />
                        )}
                      </VStack>
                      <Stack gap={0} pt={4}>
                        {isLoading ? <Spinner /> : null}
                        {menuItems.map((mi) => (
                          <NavButton
                            key={mi.label}
                            variant="ghost"
                            borderRadius={0}
                            label={mi.label}
                            height={12}
                            icon={mi.icon}
                            route={mi.url(event_id)}
                            isActive={
                              !!matchPath(location.pathname, mi.url(event_id))
                            }
                            iconProps={{
                              _active: {
                                color: "white",
                              },
                            }}
                            _active={{
                              background: "black",
                              color: "white",
                            }}
                          />
                        ))}
                      </Stack>
                    </Stack>
                  </Stack>
                </Flex>
              </Flex>
            </motion.div>
          ) : (
            <motion.div
              transition={{
                type: "tween",
                duration: 0.3,
                delay: isCollapsed ? 0 : 0.3,
              }}
              key={"event-detail-sidebar-mini"}
              style={{
                position: "absolute",
                height: "100%",
                left: 0,
                top: 0,
                zIndex: 9,
              }}
              initial={{ translateX: "-100%" }}
              animate={{ translateX: 0 }}
              exit={{ translateX: "-100%" }}
            >
              <Flex as="section" height="100%" bg="bg-canvas" width="50px">
                <Flex
                  flex="1"
                  bg="bg-surface"
                  overflowY="auto"
                  boxShadow={colorModeValue}
                >
                  <Stack justify="space-between" spacing="1" width="full">
                    <Stack
                      spacing={{ base: "5", sm: "6" }}
                      shouldWrapChildren
                      width="full"
                    >
                      <VStack h={16} pt={4}>
                        {isCollapsed ? (
                          <IconButton
                            variant="ghost"
                            aria-label="Open"
                            icon={<FiArrowLeft />}
                            onClick={() => setIsCollapsed(false)}
                          />
                        ) : (
                          <IconButton
                            aria-label="Close"
                            variant="ghost"
                            icon={<FiArrowRight />}
                            onClick={() => setIsCollapsed(true)}
                          />
                        )}
                      </VStack>
                      <Stack gap={0} pt={4}>
                        {menuItems.map((mi) => (
                          <IconButton
                            borderRadius={0}
                            _active={{
                              background: "black",
                            }}
                            isActive={
                              !!matchPath(location.pathname, mi.url(event_id))
                            }
                            aria-label="Event details"
                            onClick={() => navigate(mi.url(event_id))}
                            size="lg"
                            bg="transparent"
                            icon={
                              <mi.icon
                                color={
                                  !!matchPath(
                                    location.pathname,
                                    mi.url(event_id)
                                  )
                                    ? "white"
                                    : "black"
                                }
                              />
                            }
                          />
                        ))}
                      </Stack>
                    </Stack>
                  </Stack>
                </Flex>
              </Flex>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </Box>
  );
};

export const MobileNavBar = ({
  show = true,
  nav = [],
  isLoading,
}: {
  show: boolean;
  isLoading: boolean;
  nav?: KYDAdminEventDetailNav[];
}) => {
  const { event_id } = useParams();

  const menuItems = useMemo(() => {
    return getMenuItems({ nav });
  }, [nav]);

  const [selectedMenu, setSelectedMenu] = useState<string>(
    menuItems.at(0)?.url(event_id)
  );

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!menuItems) return;

    menuItems.find((mi) => {
      const isItemSelected = matchPath(location.pathname, mi.url(event_id));

      if (isItemSelected) {
        setSelectedMenu(mi.url(event_id));

        return true;
      }

      return false;
    });
  }, [location, event_id]);

  const navigateToItem = (url: string) => {
    navigate(url);
  };

  if (!show) {
    return null;
  }

  return (
    <Box>
      <Select
        w={"full"}
        value={selectedMenu}
        onChange={(e) => navigateToItem(e.currentTarget.value)}
      >
        {isLoading ? <Spinner /> : null}
        {menuItems.map((mi) => (
          <option key={mi.label} value={mi.url(event_id)}>
            {mi.label}
          </option>
        ))}
      </Select>
    </Box>
  );
};
